import { Injectable, inject } from '@angular/core';
import { ApiService, LogLevel, LoggerService, SessionService } from '@simac/ui';
import { IDevicePost, ISessionInitialized } from '../models/flutter';
import { NotificcationSignalingService } from '../signalling/notification-signalling.service';

declare let window: any;

@Injectable({
  providedIn: 'root',
})
export class FlutterService {
  private readonly apiService = inject(ApiService);
  private readonly loggerService = inject(LoggerService);
  private readonly sessionService = inject(SessionService);
  private readonly notificcationSignalingService = inject(NotificcationSignalingService);

  isFlutterApp: boolean = window.flutter_inappwebview !== undefined;

  private messagesToLog: string[] = [];
  constructor() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.appToBackground();
      } else if (document.visibilityState === 'visible') {
        this.appToForeground();
      }
    });
  }

  getGeolocation(): Promise<any> | undefined {
    if (this.isFlutterApp) {
      return window.flutter_inappwebview.callHandler('getCurrentPosition');
    }
    return undefined;
  }

  onSessionInitialized(): void {
    if (this.isFlutterApp) {
      window.flutter_inappwebview.callHandler('onSessionInitialized').then((result: ISessionInitialized) => {
        this.saveDevice(result.fcmToken, result.deviceName);
        this.logAppInfo(result.buildNumber, result.version);
      });
      if (this.messagesToLog.length > 0) {
        this.messagesToLog.forEach((message) => {
          this.log(message);
        });
        this.messagesToLog = [];
      }
    }
  }

  onSessionExpired(): void {
    if (this.isFlutterApp) {
      return window.flutter_inappwebview.callHandler('onSessionExpired');
    }
  }

  private appToForeground(): void {
    if (this.isFlutterApp && this.sessionService.session) {
      this.notificcationSignalingService.connectToNotificationHub();
    }
  }

  private appToBackground(): void {
    if (this.isFlutterApp && this.sessionService.session) {
      this.notificcationSignalingService.stopAllHubConnections();
    }
  }

  private log(message: string): void {
    if (!this.sessionService.session) {
      this.messagesToLog.push(message);
      return;
    }

    this.loggerService
      .log({
        message: message,
        level: LogLevel.Information,
        properties: [],
      })
      .subscribe();
  }

  private saveDevice(token: string, deviceName: string): void {
    this.apiService.post<IDevicePost, void>('api/user/device', { token, name: deviceName }).subscribe();
  }

  private logAppInfo(buildNumber: string, version: string): void {
    const session = this.sessionService.session;
    let properties = [
      {
        key: 'Build',
        value: buildNumber,
      },
      {
        key: 'Version',
        value: version,
      },
    ];

    if (session) {
      properties.push(
        {
          key: 'projectId',
          value: session.projectId?.toString() ?? '',
        },
        {
          key: 'projectName',
          value: session.projectName ?? '',
        },
        {
          key: 'userId',
          value: session.userId,
        },
        {
          key: 'userEmail',
          value: session.userEmail,
        },
      );
    }

    this.loggerService
      .log({
        message: 'App info',
        level: LogLevel.Information,
        properties: properties,
      })
      .subscribe();
  }
}
